import { useState, useRef } from 'react'
import { useI18n } from '../../i18n'
import { Midi } from '@tonejs/midi'
import { SECTION_TYPES, INSTRUMENT_TYPES, IMAGE_MAX_WIDTH, IMAGE_MAX_HEIGHT, MIDI_DEFAULT_TEMPO } from '../../config/songbook'
import { useSongStore } from '../../store/useSongStore'
import { SectionButton } from './SectionButton'
import { ChordInput } from './ChordInput'

interface ActionToolbarProps {
  activeSectionId: string | null
}

export function ActionToolbar({ activeSectionId }: ActionToolbarProps) {
  const { t } = useI18n()
  const [showChordInput, setShowChordInput] = useState(false)
  const [customDialog, setCustomDialog] = useState<'section' | 'instrument' | null>(null)
  const [customName, setCustomName] = useState('')
  // When a preset section button is clicked, we remember its type so
  // the submit handler creates the right sectionTypeId + color.
  const [pendingSectionTypeId, setPendingSectionTypeId] = useState<string>('custom')
  const [isUploading, setIsUploading] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const midiInputRef = useRef<HTMLInputElement>(null)
  const { currentSong, addElement, insertElementInSection } = useSongStore()

  const genId = () => crypto.randomUUID()

  const add = (element: Parameters<typeof addElement>[0]) => {
    if (activeSectionId) {
      insertElementInSection(element, activeSectionId)
    } else {
      addElement(element)
    }
  }

  const handleAddSection = (sectionTypeId: string, name?: string) => {
    if (!currentSong) return
    // Resolve the display name: explicit name for custom, translated
    // preset name for everything else. Every section is editable via
    // double-tap, no auto-numbering.
    const cfg = SECTION_TYPES.find((s) => s.id === sectionTypeId)
    const customName = name
      ?? (cfg && sectionTypeId !== 'custom'
        ? t(cfg.i18nKey as Parameters<typeof t>[0])
        : undefined)
    addElement({
      id: genId(),
      type: 'section',
      sectionTypeId,
      number: 1,
      ...(customName ? { customName } : {}),
    })
  }

  const handleAddInstrument = (instrumentTypeId: string, name?: string) => {
    if (!currentSong) return
    const count = currentSong.elements.filter(
      (e) => e.type === 'instrument' && e.instrumentTypeId === instrumentTypeId
        && (instrumentTypeId !== 'custom' || e.customName === name)
    ).length
    add({
      id: genId(),
      type: 'instrument',
      instrumentTypeId,
      number: count + 1,
      ...(name ? { customName: name } : {}),
    })
  }

  const handleAddChord = (name: string) => {
    add({ id: genId(), type: 'chord', name })
  }

  const handleAddTab = () => {
    add({ id: genId(), type: 'tab', dots: [] })
  }

  const handleAddTablature = () => {
    add({ id: genId(), type: 'tablature', columns: [{ frets: [null, null, null, null, null, null] }] })
  }

  const handleAddImage = async (file: File) => {
    if (!currentSong) return
    setIsUploading(true)

    try {
      // Resize image on canvas
      const img = await loadImage(file)
      const canvas = document.createElement('canvas')
      let w = img.width
      let h = img.height

      if (w > IMAGE_MAX_WIDTH) {
        h = Math.round(h * (IMAGE_MAX_WIDTH / w))
        w = IMAGE_MAX_WIDTH
      }
      if (h > IMAGE_MAX_HEIGHT) {
        w = Math.round(w * (IMAGE_MAX_HEIGHT / h))
        h = IMAGE_MAX_HEIGHT
      }

      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, w, h)

      const blob = await new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.85)
      )

      // Store image as base64 data URL directly in the song element.
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.readAsDataURL(blob)
      })

      const fileId = genId()
      add({
        id:          fileId,
        type:        'image',
        storagePath: dataUrl,
        width:       w,
        height:      h,
      })
    } catch (e) {
      console.error('Image processing error:', e)
    } finally {
      setIsUploading(false)
    }
  }

  const handleAddMidi = async (file: File) => {
    try {
      const arrayBuffer = await file.arrayBuffer()
      const midi = new Midi(arrayBuffer)

      const tempo = midi.header.tempos.length > 0
        ? Math.round(midi.header.tempos[0].bpm)
        : MIDI_DEFAULT_TEMPO

      const tracks = midi.tracks
        .filter((t) => t.notes.length > 0)
        .map((t) => ({
          name: t.name || 'Track',
          notes: t.notes.map((n) => ({
            midi: n.midi,
            time: n.time,
            duration: n.duration,
            velocity: n.velocity,
          })),
        }))

      if (tracks.length === 0) return

      const maxTime = Math.max(
        ...tracks.flatMap((t) => t.notes.map((n) => n.time + n.duration))
      )

      add({
        id: genId(),
        type: 'midi',
        filename: file.name,
        tracks,
        tempo,
        durationSeconds: maxTime,
      })
    } catch (e) {
      console.error('MIDI parse error:', e)
    }
  }

  const handleCustomSubmit = () => {
    const trimmed = customName.trim()
    if (!trimmed) return
    if (customDialog === 'section') {
      handleAddSection(pendingSectionTypeId, trimmed)
    } else {
      handleAddInstrument('custom', trimmed)
    }
    setCustomName('')
    setCustomDialog(null)
    setPendingSectionTypeId('custom')
  }

  const customPlaceholder = customDialog === 'section'
    ? t('songbook.customSectionName')
    : t('songbook.customInstrumentName')

  return (
    <>
      {showChordInput && (
        <ChordInput
          onAdd={handleAddChord}
          onClose={() => setShowChordInput(false)}
        />
      )}

      {/* Custom name dialog (for section or instrument) */}
      {customDialog && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => setCustomDialog(null)}>
          <div
            className="w-full rounded-t-2xl border-t border-surface-lighter bg-surface-light p-4 pb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-surface-lighter" />
            <div className="flex gap-2">
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCustomSubmit()}
                placeholder={customPlaceholder}
                className="flex-1 rounded-lg border-2 border-surface-lighter bg-surface px-3 py-2.5 text-text placeholder-text-muted focus:border-primary focus:outline-none"
                autoFocus
              />
              <button
                className="shrink-0 rounded-lg bg-primary px-4 py-2.5 font-medium text-white active:bg-primary-dark"
                onClick={handleCustomSubmit}
              >
                {t('songbook.add')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="shrink-0 border-t border-surface-lighter bg-surface pb-[env(safe-area-inset-bottom)]">
        {/* Row 1: Section buttons */}
        <div className="flex flex-wrap gap-1.5 px-3 pt-2">
          {SECTION_TYPES.map((cfg) => (
            <SectionButton
              key={cfg.id}
              config={cfg}
              onAdd={() => {
                setPendingSectionTypeId(cfg.id)
                setCustomName(
                  cfg.id === 'custom'
                    ? ''
                    : t(cfg.i18nKey as Parameters<typeof t>[0]),
                )
                setCustomDialog('section')
              }}
            />
          ))}
        </div>

        {/* Row 2: Instrument buttons */}
        <div className="flex flex-wrap gap-1.5 px-3 pt-1.5">
          {INSTRUMENT_TYPES.map((cfg) => (
            <button
              key={cfg.id}
              className="shrink-0 rounded-lg border border-surface-lighter bg-surface-light px-2.5 py-1.5 text-xs font-medium text-text active:bg-surface-lighter"
              onClick={() => {
                if (cfg.id === 'custom') {
                  setCustomDialog('instrument')
                } else {
                  handleAddInstrument(cfg.id)
                }
              }}
            >
              {t(cfg.i18nKey as Parameters<typeof t>[0])}
            </button>
          ))}
        </div>

        {/* Row 3: Chord + Tab + Tablature + Image */}
        <div className="flex gap-1.5 px-3 pb-2 pt-1.5">
          <button
            className="flex-1 rounded-lg border-2 border-primary bg-transparent py-2 text-xs font-medium text-primary active:bg-primary active:text-white"
            onClick={() => setShowChordInput(true)}
          >
            {t('songbook.addChord')}
          </button>
          <button
            className="flex-1 rounded-lg border-2 border-primary bg-transparent py-2 text-xs font-medium text-primary active:bg-primary active:text-white"
            onClick={handleAddTab}
          >
            {t('songbook.addTab')}
          </button>
          <button
            className="flex-1 rounded-lg border-2 border-primary bg-transparent py-2 text-xs font-medium text-primary active:bg-primary active:text-white"
            onClick={handleAddTablature}
          >
            {t('songbook.addTablature')}
          </button>
          <button
            className="flex-1 rounded-lg border-2 border-primary bg-transparent py-2 text-xs font-medium text-primary active:bg-primary active:text-white disabled:opacity-50"
            onClick={() => imageInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? '...' : t('songbook.addImage')}
          </button>
          <button
            className="flex-1 rounded-lg border-2 border-primary bg-transparent py-2 text-xs font-medium text-primary active:bg-primary active:text-white"
            onClick={() => midiInputRef.current?.click()}
          >
            {t('songbook.addMidi')}
          </button>
          <input
            ref={midiInputRef}
            type="file"
            accept=".mid,.midi"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (file) await handleAddMidi(file)
              if (midiInputRef.current) midiInputRef.current.value = ''
            }}
            className="hidden"
          />
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (file) await handleAddImage(file)
              if (imageInputRef.current) imageInputRef.current.value = ''
            }}
            className="hidden"
          />
        </div>
      </div>
    </>
  )
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = URL.createObjectURL(file)
  })
}
